import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import LoadingSpinner from '../components/LoadingSpinner';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const sessionReady = useRef(false);

  useEffect(() => {
    // Listen for auth state changes - the recovery link triggers PASSWORD_RECOVERY event
    // This is more reliable than getSession() which may race with token exchange
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (sessionReady.current) return;

      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        sessionReady.current = true;
        setError('');
        setIsVerifying(false);
      }
    });

    // Also check for an already-existing session (e.g. page refresh after recovery)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session && !sessionReady.current) {
        sessionReady.current = true;
        setIsVerifying(false);
      }
    });

    // Timeout: if no valid session after 5 seconds, show error
    const timeout = setTimeout(() => {
      if (!sessionReady.current) {
        setError(t('auth.invalidExpiredResetLink'));
        setIsVerifying(false);
      }
    }, 5000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return t('auth.passwordMinEight');
    }
    if (!/[A-Z]/.test(pwd)) {
      return t('auth.passwordNeedsUppercase');
    }
    if (!/[a-z]/.test(pwd)) {
      return t('auth.passwordNeedsLowercase');
    }
    if (!/[0-9]/.test(pwd)) {
      return t('auth.passwordNeedsNumber');
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      setIsLoading(false);
      return;
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      setIsLoading(false);
      return;
    }

    try {
      // Update the user's password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        throw updateError;
      }

      setSuccess(true);

      // Sign out and redirect to login after 2 seconds
      // Use window.location for a hard redirect to avoid auth state race conditions
      // nosemgrep: javascript.lang.security.detect-eval-with-expression.detect-eval-with-expression
      setTimeout(async () => {
        await supabase.auth.signOut();
        window.location.href = '/login';
      }, 2000);
    } catch (err: any) {
      console.error('Error resetting password:', err);
      if (!navigator.onLine) {
        setError(t('auth.offlineError'));
      } else {
        const msg = (err.message || '').toLowerCase();
        if (msg.includes('session') || msg.includes('expired') || msg.includes('token')) {
          setError(t('auth.invalidExpiredResetLink'));
        } else if (err.status === 429 || msg.includes('rate limit') || msg.includes('too many requests')) {
          setError(t('auth.resetRateLimited'));
        } else {
          setError(err.message || t('auth.resetErrorOccurred'));
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-primary/5 to-secondary/10 dark:from-background dark:via-primary/10 dark:to-secondary/5">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-muted-foreground">{t('auth.verifyingResetLink')}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex flex-col justify-center px-6 py-12 bg-gradient-to-br from-background via-primary/5 to-secondary/10 dark:from-background dark:via-primary/10 dark:to-secondary/5">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-card border border-border/50 rounded-2xl shadow-xl p-8">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>

              <h2 className="text-2xl font-bold text-foreground mb-4">
                {t('auth.passwordResetSuccess')}
              </h2>

              <p className="text-muted-foreground mb-6">
                {t('auth.passwordResetSuccessDesc')}
              </p>

              <LoadingSpinner size="md" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && isVerifying === false && !password) {
    return (
      <div className="min-h-screen flex flex-col justify-center px-6 py-12 bg-gradient-to-br from-background via-destructive/5 to-secondary/10 dark:from-background dark:via-destructive/10 dark:to-secondary/5">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-card border border-border/50 rounded-2xl shadow-xl p-8">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-6">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>

              <h2 className="text-2xl font-bold text-foreground mb-4">
                {t('auth.invalidResetLink')}
              </h2>

              <p role="alert" aria-live="assertive" className="text-muted-foreground mb-6">
                {error}
              </p>

              <button
                onClick={() => navigate('/forgot-password')}
                className="w-full bg-primary text-primary-foreground py-3 px-4 rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                {t('auth.requestNewResetLink')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center px-6 py-12 bg-gradient-to-br from-background via-primary/5 to-secondary/10 dark:from-background dark:via-primary/10 dark:to-secondary/5">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary/70 rounded-full flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-xl">GE</span>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-foreground mb-2">
            {t('auth.resetYourPassword')}
          </h2>
          <p className="text-muted-foreground">
            {t('auth.enterNewPasswordBelow')}
          </p>
        </div>

        {/* Form */}
        <div className="bg-card border border-border/50 rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div role="alert" aria-live="assertive" className="p-4 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg animate-fade-in">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                {t('auth.newPassword')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-muted-foreground" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-10 py-3 border border-border rounded-lg bg-background/50 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder={t('auth.enterNewPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-muted-foreground hover:text-foreground" />
                  ) : (
                    <Eye className="h-5 w-5 text-muted-foreground hover:text-foreground" />
                  )}
                </button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {t('auth.passwordRequirements')}
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-2">
                {t('auth.confirmPassword')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-muted-foreground" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full pl-10 pr-10 py-3 border border-border rounded-lg bg-background/50 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder={t('auth.confirmNewPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-muted-foreground hover:text-foreground" />
                  ) : (
                    <Eye className="h-5 w-5 text-muted-foreground hover:text-foreground" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <LoadingSpinner size="sm" />
                  <span>{t('auth.resettingPassword')}</span>
                </div>
              ) : (
                t('auth.resetPassword')
              )}
            </button>
          </form>
        </div>

        {/* Help */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            {t('auth.rememberPassword')} {' '}
            <Link to="/login" className="font-medium text-primary hover:text-primary/80 transition-colors">
              {t('auth.backToLogin')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
